import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'jotai';
import './styles.scss';

import { auth as firebaseAuth } from './utils/firebaseSetUp';
import { isSelfHosted, AUTH_SESSION_EVENT } from './config/infra';
import { getSelfHostedUser } from './api/selfHostedAuth';
import { onAuthStateChanged } from 'firebase/auth';
import Loading from './components/shared/loading/Loading';
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OrganizationSelectorRoute from './components/auth/OrganizationSelectorRoute';
import NavigationHandler from './components/layout/NavigationHandler';
import Orders from './components/orders/Orders';
import Quotations from './components/quotations/Quotations';
import ClientOrders from './components/clientOrders/ClientOrders';
import Returns from './components/returns/Returns';
import Toaster from './components/shared/ui/Toaster';
import ConfirmationDialog from './components/shared/dialogs/ConfirmationDialog';
import { Organization, getOrganization, getOrganizationsByIds, getUser, getCurrentUser, getCurrentUserWithRetry, acceptUserLegal } from './api';
import {
  claimsIndicateGodRole,
  getRequestedOrganizationIdFromSearch,
  resolveFirebaseAdminAccess,
  resolveOrganizationIdsForBootstrap,
  resolveSelfHostedAdminAccess,
  syncLegalGate,
  type GlobalLegalState,
} from './utils/authBootstrap';
import {
  extractOrgPanelAccess,
  resolveEffectivePermissions,
} from './utils/panelPermissions';
import { PanelAccessProvider } from './contexts/PanelAccessContext';
import CompleteProfile from './components/auth/CompleteProfile';

import GlobalLoading from './components/shared/loading/GlobalLoading';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { Button, Modal, Tab, Tabs } from 'react-bootstrap';

//Components

const Auth = lazy(() => import('./components/auth/Auth'));
const SignupPage = lazy(() => import('./components/auth/SignupPage'));
const Inventory = lazy(() => import('./components/inventory/Inventory'));
const Clients = lazy(() => import('./components/clients/Clients'));
const Configuration = lazy(() => import('./components/settings/Configuration'));
const Home = lazy(() => import('./components/shared/ui/Home'));
const SalesPeople = lazy(() => import('./components/vendors/SalesPeople'));
const Paywall = lazy(() => import('./components/payments/Paywall'));
const AccountsReceivablePage = lazy(
  () => import('./components/accountsReceivable')
);
const AccountsPayablePage = lazy(
  () => import('./components/accountsPayable')
);
const SuppliersPage = lazy(
  () => import('./components/purchasing/suppliers/SuppliersPage')
);
const PurchasesPage = lazy(
  () => import('./components/purchasing/purchases/PurchasesPage')
);
const PaywallSuccess = lazy(
  () => import('./components/payments/PaywallSuccess')
);
type ToastVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'dark';

interface ToastItem {
  id: number;
  message: string;
  variant?: ToastVariant;
  title?: string;
  autoHide?: number;
}

interface ToastOptions {
  variant?: ToastVariant;
  title?: string;
  autoHide?: number;
}

interface AlertState {
  isOpen: boolean;
  intent: 'danger' | 'warning' | 'info' | 'success';
  text: string;
  icon: any;
  success: (callback?: () => void) => void;
}

const getRequestedOrganizationIdFromQuery = (): string | null => {
  if (typeof window === 'undefined') return null;
  return getRequestedOrganizationIdFromSearch(window.location.search);
};

interface AuthenticatedAppShellProps {
  organization: Organization | null;
  userOrganizations: Organization[];
  onOrganizationChange: (organizationId: string) => void;
  onProfileComplete: () => Promise<void>;
  addToast: (message: string, options?: ToastOptions) => void;
  device: string;
  navStatus: string;
  setNavStatus: (status: string) => void;
  isAdmin: boolean;
  panelPermissions?: string[] | null;
  isGodMode?: boolean;
}

const AuthenticatedAppShell: React.FC<AuthenticatedAppShellProps> = ({
  organization,
  userOrganizations,
  onOrganizationChange,
  onProfileComplete,
  addToast,
  device,
  navStatus,
  setNavStatus,
  isAdmin,
  panelPermissions = null,
  isGodMode = false,
}) => {
  const location = useLocation();
  const isPaywallRoute = location.pathname.startsWith('/paywall');
  const showCompleteProfile =
    organization &&
    organization.isProfileComplete === false &&
    !isPaywallRoute;

  if (showCompleteProfile) {
    return (
      <CompleteProfile
        organization={organization}
        onComplete={onProfileComplete}
        addToast={addToast}
      />
    );
  }

  return (
    <MainLayout
      organization={organization}
      userOrganizations={userOrganizations}
      onOrganizationChange={onOrganizationChange}
      device={device}
      navStatus={navStatus}
      setNavStatus={setNavStatus}
      isAdmin={isAdmin}
      panelPermissions={panelPermissions}
      isGodMode={isGodMode}
    />
  );
};

const App: React.FC = () => {
  // State hooks
  const [auth, setAuth] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true); // Track auth initialization
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [admin, setAdmin] = useState<boolean>(false);
  const [godMode, setGodMode] = useState<boolean>(false);
  const [panelPermissions, setPanelPermissions] = useState<string[] | null>(
    null
  );
  const [toast, setToast] = useState<{ state: boolean; message: string }>({
    state: false,
    message: '',
  });
  const [alert, setAlert] = useState<AlertState>({
    isOpen: false,
    intent: 'info',
    text: '',
    icon: 'issue',
    success: () => {},
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [navStatus, setNavStatus] = useState<string>('expanded');
  const [device, setDevice] = useState<string>('expanded');
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [brandingLoaded, setBrandingLoaded] = useState<boolean>(false);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>(
    []
  );
  const [showOrgSelector, setShowOrgSelector] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const applyPanelAccess = useCallback(
    (
      userData: {
        panelRole?: string | null;
        permissions?: string[] | null;
        effectivePermissions?: string[] | null;
        panelAccessByOrg?: Record<
          string,
          { panelRole?: string | null; permissions?: string[] | null }
        > | null;
      } | null,
      isGodMode: boolean,
      organizationId?: string | null
    ) => {
      // Prefer server-resolved list when present (already scoped if /me?organizationId=).
      if (Array.isArray(userData?.effectivePermissions)) {
        setPanelPermissions(userData.effectivePermissions);
        return;
      }
      const scoped = extractOrgPanelAccess(userData, organizationId);
      setPanelPermissions(
        resolveEffectivePermissions({
          panelRole: scoped.panelRole ?? userData?.panelRole,
          permissions: scoped.permissions ?? userData?.permissions,
          isGodMode,
        })
      );
    },
    []
  );
  const [pendingRequestedOrganizationId, setPendingRequestedOrganizationId] =
    useState<string | null>(null);
  const currentOrganizationRef = useRef<Organization | null>(null);

  useEffect(() => {
    currentOrganizationRef.current = organization;
  }, [organization]);

  // Re-resolve panel permissions whenever the active organization changes.
  useEffect(() => {
    if (!auth || !admin || godMode || !organization?.id || !currentUserId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const userData = await getCurrentUser(organization.id);
        if (!cancelled && userData) {
          applyPanelAccess(userData, godMode, organization.id);
        }
      } catch (error) {
        console.warn('Failed to refresh panel permissions for org', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, admin, godMode, organization?.id, currentUserId, applyPanelAccess]);

  // Legal gating (force re-accept when versions change). Default to Spanish.
  const getDefaultLegalLocale = (): 'es' | 'en' => 'es';
  const [legalGateOpen, setLegalGateOpen] = useState(false);
  const [legalLocale, setLegalLocale] = useState<'es' | 'en'>(
    getDefaultLegalLocale
  );
  const [legalTab, setLegalTab] = useState<'terms' | 'privacy'>('terms');
  const [globalLegal, setGlobalLegal] = useState<GlobalLegalState | null>(null);

  const LEGAL_URLS = {
    terms: {
      es: 'https://mayorx.net/terminos',
      en: 'https://mayorx.net/terms',
    },
    privacy: {
      en: 'https://mayorx.net/privacy-policy',
      es: 'https://mayorx.net/privacy-policy-es',
    },
  } as const;

  // Alert dialog refs
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Callback functions
  const addToast = useCallback((message: string, options?: ToastOptions) => {
    const newToast: ToastItem = {
      id: new Date().getTime(),
      message,
      variant: options?.variant,
      title: options?.title,
      autoHide: options?.autoHide ?? 5000,
    };
    setToasts(prevToasts => [...prevToasts, newToast]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const handlePendingOrganizationAccess = useCallback((organizationId: string) => {
    const normalized = organizationId.trim();
    if (!normalized) return;
    setPendingRequestedOrganizationId(normalized);
  }, []);

  const handleSetNavStatus = useCallback((status: string) => {
    setNavStatus(status);
  }, []);

  const openAlert = useCallback(
    (
      state: boolean,
      text: string,
      icon: any,
      intent: any,
      cb: (callback?: () => void) => void
    ) => {
      setAlert({
        isOpen: state,
        text: text,
        icon: icon,
        intent: intent,
        success: cb,
      });
    },
    []
  );

  const handleOrganizationSelection = useCallback(
    async (organizationId: string): Promise<void> => {
      try {
        const selectedOrg = userOrganizations.find(
          org => org.id === organizationId
        );
        if (selectedOrg) {
          setOrganization(selectedOrg);
          setShowOrgSelector(false);
          // Panel permissions refresh via the organization.id effect below.
          addToast(`Organización seleccionada: ${selectedOrg.name}`);
        }
      } catch (error) {
        console.error('Error selecting organization:', error);
        addToast('Error al seleccionar organización');
      }
    },
    [userOrganizations, addToast]
  );

  const handleOrganizationRefresh = useCallback(async () => {
    // Reload user organizations to get the latest data
    if (currentUserId) {
      try {
        const userData = await getUser(currentUserId);
        if (userData) {
          const organizationIds = userData.organizationIds || [];
          if (organizationIds.length > 0) {
            const orgs = await getOrganizationsByIds(organizationIds);
            setUserOrganizations(orgs);

            // Update current organization if it exists
            if (organization) {
              const updatedOrg = orgs.find(org => org.id === organization.id);
              if (updatedOrg) {
                setOrganization(updatedOrg);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing organization data:', error);
      }
    }
  }, [currentUserId, organization]);

  // Effects
  useEffect(() => {
    // Media query listener
    const handleMediaQuery = (x: MediaQueryListEvent | MediaQueryList) => {
      if (x.matches) {
        setDevice('small');
      } else {
        setDevice('expanded');
      }
    };

    const mediaQuery = window.matchMedia('(max-width: 800px)');
    handleMediaQuery(mediaQuery); // Call listener function at run time

    // Use the modern addEventListener instead of deprecated addListener
    mediaQuery.addEventListener('change', handleMediaQuery);

    let unsubscribe = () => {};

    const finishAuthLoading = () => setAuthLoading(false);

    if (isSelfHosted) {
      const runSelfHostedBootstrap = async () => {
        setAuthLoading(true);
        const stored = getSelfHostedUser();
        if (!stored?.uid) {
          setAuth(false);
          setAdmin(false);
          setGodMode(false);
          setPanelPermissions(null);
          setOrganization(null);
          setUserOrganizations([]);
          setShowOrgSelector(false);
          setCurrentUserId(null);
          finishAuthLoading();
          return;
        }
        try {
          let userData = null as Awaited<ReturnType<typeof getCurrentUserWithRetry>>;
          try {
            userData = await getCurrentUserWithRetry();
          } catch (error) {
            console.error('Selfhosted user fetch failed:', error);
          }
          const storedUser = stored;
          const { isGodMode, isAdminUser, canAccess } =
            resolveSelfHostedAdminAccess(userData, storedUser);
          if (canAccess) {
            setAuth(true);
            setAdmin(true);
            setGodMode(isGodMode);
            setCurrentUserId(storedUser?.uid ?? userData?.id ?? stored.uid);
            applyPanelAccess(userData, isGodMode, organization?.id);

            await syncLegalGate(
              userData,
              isGodMode,
              setGlobalLegal,
              setLegalGateOpen
            );

            try {
              const requestedOrganizationId = isGodMode
                ? getRequestedOrganizationIdFromQuery() ??
                  pendingRequestedOrganizationId
                : null;
              const organizationIds = resolveOrganizationIdsForBootstrap({
                userOrganizationIds: userData?.organizationIds,
                storedOrganizationId: storedUser?.organizationId,
                sessionOrganizationId: stored.organizationId,
              });
              const orgsFromMembership =
                organizationIds.length > 0
                  ? await getOrganizationsByIds(organizationIds)
                  : [];

              if (requestedOrganizationId) {
                const requestedOrganization = await getOrganization(
                  requestedOrganizationId
                );
                if (requestedOrganization) {
                  const nextOrganizations = orgsFromMembership.some(
                    org => org.id === requestedOrganization.id
                  )
                    ? orgsFromMembership
                    : [requestedOrganization, ...orgsFromMembership];
                  setUserOrganizations(nextOrganizations);
                  setOrganization(requestedOrganization);
                  setShowOrgSelector(false);
                  setPendingRequestedOrganizationId(null);
                } else {
                  setUserOrganizations(orgsFromMembership);
                  setOrganization(null);
                  setShowOrgSelector(orgsFromMembership.length > 1);
                  addToast(
                    `No existe la organización solicitada (${requestedOrganizationId}).`
                  );
                  setPendingRequestedOrganizationId(null);
                }
              } else {
                setUserOrganizations(orgsFromMembership);
                if (orgsFromMembership.length === 1) {
                  setOrganization(orgsFromMembership[0]);
                  setShowOrgSelector(false);
                } else if (orgsFromMembership.length > 1) {
                  setShowOrgSelector(true);
                } else if (isGodMode) {
                  const currentOrganization = currentOrganizationRef.current;
                  if (currentOrganization) {
                    setOrganization(currentOrganization);
                    setUserOrganizations([currentOrganization]);
                    setShowOrgSelector(false);
                  } else {
                    setOrganization(null);
                    setShowOrgSelector(true);
                    addToast(
                      'Añade ?organizationId=<id> en la URL para abrir una organización.'
                    );
                  }
                } else {
                  addToast('No tienes organizaciones asignadas');
                }
              }
            } catch (error) {
              console.error('Error loading user organizations:', error);
              addToast('Error al cargar organizaciones');
            }
          } else {
            setAuth(false);
            setAdmin(false);
            setGodMode(false);
            setPanelPermissions(null);
          }
        } catch (error) {
          console.error('Selfhosted auth bootstrap failed:', error);
          setAuth(false);
          setAdmin(false);
          setGodMode(false);
          setPanelPermissions(null);
        } finally {
          finishAuthLoading();
        }
      };

      void runSelfHostedBootstrap();
      const onSessionChange = () => {
        void runSelfHostedBootstrap();
      };
      window.addEventListener(AUTH_SESSION_EVENT, onSessionChange);

      return () => {
        mediaQuery.removeEventListener('change', handleMediaQuery);
        window.removeEventListener(AUTH_SESSION_EVENT, onSessionChange);
      };
    } else {
    // Firebase auth state listener
    unsubscribe = onAuthStateChanged(firebaseAuth, async user => {
      if (user) {
        // User is signed in.
        try {
          // Read custom claims first - this is the source of truth for
          // the platform "god" role (it is not stored in the Firestore user doc).
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims as Record<string, unknown>;
          const isGodFromClaims = claimsIndicateGodRole(claims);

          // Server creates the user profile after auth (e.g. Google sign-up). Retry briefly.
          const userData = await getCurrentUserWithRetry(
            isGodFromClaims ? { attempts: 1 } : undefined
          );

          const { isGodMode, isAdminUser, canAccess } = resolveFirebaseAdminAccess(
            isGodFromClaims,
            userData
          );

          if (canAccess) {
            setAuth(true);
            setAdmin(true);
            setGodMode(isGodMode);
            setCurrentUserId(user.uid);
            applyPanelAccess(userData, isGodMode, organization?.id);

            await syncLegalGate(
              userData,
              isGodMode,
              setGlobalLegal,
              setLegalGateOpen
            );

            // Get user's organizations
            try {
              const requestedOrganizationId = isGodMode
                ? getRequestedOrganizationIdFromQuery() ??
                  pendingRequestedOrganizationId
                : null;
              const organizationIds: string[] = userData?.organizationIds || [];
              const orgsFromMembership =
                organizationIds.length > 0
                  ? await getOrganizationsByIds(organizationIds)
                  : [];

              if (requestedOrganizationId) {
                const requestedOrganization = await getOrganization(
                  requestedOrganizationId
                );
                if (requestedOrganization) {
                  const nextOrganizations = orgsFromMembership.some(
                    org => org.id === requestedOrganization.id
                  )
                    ? orgsFromMembership
                    : [requestedOrganization, ...orgsFromMembership];
                  setUserOrganizations(nextOrganizations);
                  setOrganization(requestedOrganization);
                  setShowOrgSelector(false);
                  setPendingRequestedOrganizationId(null);
                } else {
                  setUserOrganizations(orgsFromMembership);
                  setOrganization(null);
                  setShowOrgSelector(orgsFromMembership.length > 1);
                  addToast(
                    `No existe la organización solicitada (${requestedOrganizationId}).`
                  );
                  setPendingRequestedOrganizationId(null);
                }
              } else {
                setUserOrganizations(orgsFromMembership);
                if (orgsFromMembership.length === 1) {
                  // User has only one organization, select it automatically
                  setOrganization(orgsFromMembership[0]);
                  setShowOrgSelector(false);
                } else if (orgsFromMembership.length > 1) {
                  // User has multiple organizations, show selector
                  setShowOrgSelector(true);
                } else if (isGodMode) {
                  // Keep previously selected org if auth fires again (token refresh/revalidation).
                  const currentOrganization = currentOrganizationRef.current;
                  if (currentOrganization) {
                    setOrganization(currentOrganization);
                    setUserOrganizations([currentOrganization]);
                    setShowOrgSelector(false);
                  } else {
                    setOrganization(null);
                    setShowOrgSelector(true);
                    addToast(
                      'Añade ?organizationId=<id> en la URL para abrir una organización.'
                    );
                  }
                } else {
                  addToast('No tienes organizaciones asignadas');
                }
              }
            } catch (error) {
              console.error('Error loading user organizations:', error);
              addToast('Error al cargar organizaciones');
            }
          } else {
            setAuth(false);
            setAdmin(false);
            setGodMode(false);
            setPanelPermissions(null);
            addToast('Solo administradores');
          }
        } catch (e: any) {
          console.log(e);
          setToast({
            state: true,
            message: 'Algo salió mal',
          });
          addToast('Algo salio mal');
        }
      } else {
        // User is signed out.
        setIsLoading(false);
        setAuth(false);
        setAdmin(false);
        setGodMode(false);
        setPanelPermissions(null);
        setOrganization(null);
        setUserOrganizations([]);
        setShowOrgSelector(false);
        setCurrentUserId(null);
        setPendingRequestedOrganizationId(null);
      }

      finishAuthLoading();
    });
    }

    // Cleanup function
    return () => {
      mediaQuery.removeEventListener('change', handleMediaQuery);
      unsubscribe();
    };
  }, [addToast, pendingRequestedOrganizationId]);

  // Load branding when organization changes
  useEffect(() => {
    if (organization) {
      setBrandingLoaded(true);
    }
  }, [organization]);

  // Get branding configuration from organization

  return (
    <Provider>
      <AuthProvider>
        <PanelAccessProvider
          permissions={panelPermissions}
          isGodMode={godMode}
        >
        <BrandingProvider organizationId={organization?.id || null}>
          <BrowserRouter>
            <NavigationHandler
              isAuthenticated={auth}
              authLoading={authLoading}
              showOrgSelector={showOrgSelector}
              hasOrganizations={userOrganizations.length}
              organization={organization}
            />
            {isLoading ? <Loading /> : null}

            <Modal
              show={legalGateOpen}
              backdrop="static"
              keyboard={false}
              centered
              size="lg"
              scrollable
            >
              <Modal.Header>
                <Modal.Title>
                  Actualización de Términos y Privacidad
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                  <div className="text-muted small">
                    Debes aceptar los documentos actualizados para continuar.
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant={
                        legalLocale === 'es' ? 'primary' : 'outline-primary'
                      }
                      onClick={() => setLegalLocale('es')}
                    >
                      ES
                    </Button>
                    <Button
                      size="sm"
                      variant={
                        legalLocale === 'en' ? 'primary' : 'outline-primary'
                      }
                      onClick={() => setLegalLocale('en')}
                    >
                      EN
                    </Button>
                  </div>
                </div>

                <Tabs
                  activeKey={legalTab}
                  onSelect={k =>
                    setLegalTab((k as 'terms' | 'privacy') || 'terms')
                  }
                  className="mb-3"
                >
                  <Tab
                    eventKey="terms"
                    title={`Términos (${globalLegal?.terms.version || '—'})`}
                  >
                    <div
                      className="border rounded p-3 bg-body-tertiary"
                      style={{ maxHeight: '55vh', overflow: 'auto' }}
                    >
                      <pre className="m-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {legalLocale === 'es'
                          ? globalLegal?.terms.es
                          : globalLegal?.terms.en}
                      </pre>
                    </div>
                  </Tab>
                  <Tab
                    eventKey="privacy"
                    title={`Privacidad (${
                      globalLegal?.privacyPolicy.version || '—'
                    })`}
                  >
                    <div
                      className="border rounded p-3 bg-body-tertiary"
                      style={{ maxHeight: '55vh', overflow: 'auto' }}
                    >
                      <pre className="m-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {legalLocale === 'es'
                          ? globalLegal?.privacyPolicy.es
                          : globalLegal?.privacyPolicy.en}
                      </pre>
                    </div>
                  </Tab>
                </Tabs>
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      window.open(
                        LEGAL_URLS.terms[legalLocale],
                        '_blank',
                        'noreferrer'
                      )
                    }
                  >
                    Abrir Términos
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      window.open(
                        LEGAL_URLS.privacy[legalLocale],
                        '_blank',
                        'noreferrer'
                      )
                    }
                  >
                    Abrir Privacidad
                  </Button>
                </div>
                <Button
                  variant="primary"
                  onClick={async () => {
                    if (!globalLegal) return;
                    try {
                      await acceptUserLegal({
                        legalLocale,
                        termsVersion: globalLegal.terms.version,
                        privacyPolicyVersion: globalLegal.privacyPolicy.version,
                      });
                      setLegalGateOpen(false);
                    } catch (e) {
                      addToast(
                        'No se pudo guardar tu aceptación. Inténtalo nuevamente.',
                        { variant: 'danger' }
                      );
                    }
                  }}
                >
                  Acepto
                </Button>
              </Modal.Footer>
            </Modal>

            <Routes>
              {/* Public route for authentication */}
              <Route
                path="/auth"
                element={
                  <Suspense fallback={<Loading />}>
                    <Auth
                      addToast={addToast}
                      onPendingOrganizationAccess={handlePendingOrganizationAccess}
                    />
                  </Suspense>
                }
              />

              {/* Public route for signup */}
              <Route
                path="/signup"
                element={
                  <Suspense fallback={<Loading />}>
                    <SignupPage addToast={addToast} />
                  </Suspense>
                }
              />

              {/* Organization selector route */}
              <Route
                path="/organization-select"
                element={
                  <OrganizationSelectorRoute
                    userOrganizations={userOrganizations}
                    onSelectOrganization={handleOrganizationSelection}
                    onClearOrganization={() => setOrganization(null)}
                    showOrgSelector={showOrgSelector}
                    isAuthenticated={auth}
                    authLoading={authLoading}
                    loading={isLoading}
                    isGodMode={godMode}
                  />
                }
              />

              {/* Protected routes with layout */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute
                    isAuthenticated={auth}
                    authLoading={authLoading}
                    isAdmin={admin}
                    panelPermissions={panelPermissions}
                    organization={organization}
                    userOrganizations={userOrganizations}
                    showOrgSelector={showOrgSelector}
                    isGodMode={godMode}
                  >
                    <AuthenticatedAppShell
                      organization={organization}
                      userOrganizations={userOrganizations}
                      onOrganizationChange={handleOrganizationSelection}
                      onProfileComplete={handleOrganizationRefresh}
                      addToast={addToast}
                      device={device}
                      navStatus={navStatus}
                      setNavStatus={handleSetNavStatus}
                      isAdmin={admin}
                      panelPermissions={panelPermissions}
                      isGodMode={godMode}
                    />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/home" replace />} />
                <Route
                  path="home"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Home
                        device={device}
                        navStatus={navStatus}
                        organization={organization}
                      />
                    </Suspense>
                  }
                />
                <Route
                  path="inventory"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <Inventory
                          openAlert={openAlert}
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization.id}
                          organization={organization}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <Orders
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization.id}
                          organization={organization}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="quotations"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <Quotations
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization.id}
                          organization={organization}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="client-orders"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <ClientOrders
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization.id}
                          organization={organization}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="returns"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <Returns
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization.id}
                          organization={organization}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />

                <Route
                  path="accounts-receivable"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <AccountsReceivablePage
                          organizationId={organization.id}
                          addToast={addToast}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="accounts-payable"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <AccountsPayablePage
                          organizationId={organization.id}
                          addToast={addToast}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="purchases-suppliers"
                  element={<Navigate to="/suppliers" replace />}
                />
                <Route
                  path="suppliers"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <SuppliersPage
                          organizationId={organization.id}
                          addToast={addToast}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="purchases"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <PurchasesPage
                          organizationId={organization.id}
                          addToast={addToast}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />
                <Route
                  path="vendors"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SalesPeople
                        device={device}
                        addToast={addToast}
                        navStatus={navStatus}
                        organizationId={organization?.id as string}
                      />
                    </Suspense>
                  }
                />

                <Route
                  path="clients"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Clients
                        device={device}
                        type={admin === true ? 'admin' : 'vendor'}
                        addToast={addToast}
                        navStatus={navStatus}
                        organizationId={organization?.id as string}
                      />
                    </Suspense>
                  }
                />
                <Route
                  path="configuration"
                  element={
                    <Suspense fallback={<Loading />}>
                      {organization ? (
                        <Configuration
                          device={device}
                          addToast={addToast}
                          navStatus={navStatus}
                          organizationId={organization?.id as string}
                          organization={organization}
                          onOrganizationUpdate={handleOrganizationRefresh}
                          currentUserId={currentUserId}
                          panelPermissions={panelPermissions}
                          isGodMode={godMode}
                        />
                      ) : (
                        <Navigate to="/organization-select" replace />
                      )}
                    </Suspense>
                  }
                />

                {/* Paywall route */}
                <Route
                  path="paywall"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Paywall
                        organizationId={organization?.id}
                        onSubscriptionUpdated={handleOrganizationRefresh}
                      />
                    </Suspense>
                  }
                />
                <Route
                  path="paywall/success"
                  element={
                    <Suspense fallback={<Loading />}>
                      <PaywallSuccess />
                    </Suspense>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
          <GlobalLoading />
          <Toaster toasts={toasts} removeToast={removeToast} />
          <ConfirmationDialog />
        </BrandingProvider>
        </PanelAccessProvider>
      </AuthProvider>
    </Provider>
  );
};

export default App;
